import { useEffect, useRef, useState } from 'react';
import { Search, X, ChevronDown } from 'lucide-react';
import { Monogram } from './Monogram';
import { loadDiscoveryDataset } from '../lib/schoolDiscovery';
import type { DiscoverySchool } from '../lib/schoolDiscoveryTypes';

// =============================================================================
// SchoolPicker (v0.2.36)
//
// A dropdown/search for linking a field to a real school by NAME. Used for the
// scholarship "School" field (requires_admission_to), which stores a school
// name string. Picking from a list instead of free-typing prevents the
// linkage issues typos cause ("UNCC" vs "UNC Charlotte" vs a misspelling).
//
// Sources, in order of convenience:
//  - the applicant's target schools (passed in) — the most likely choices,
//    shown first as quick picks;
//  - the full bundled school dataset, via type-ahead search.
//
// The value is the school name string (matching the field's existing type).
// =============================================================================

interface Props {
  /** Current value — a school name, or null/empty. */
  value: string | null;
  onChange: (value: string | null) => void;
  /** The applicant's target-school names, offered as quick picks. */
  targetSchoolNames?: string[];
  id?: string;
}

export function SchoolPicker({ value, onChange, targetSchoolNames = [], id }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [allSchools, setAllSchools] = useState<DiscoverySchool[] | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load the bundled dataset lazily — only when the picker is first opened.
  useEffect(() => {
    if (!open || allSchools) return;
    loadDiscoveryDataset()
      .then((d) => setAllSchools(d.schools))
      .catch(() => setAllSchools([]));
  }, [open, allSchools]);

  // Close on outside click.
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const q = query.trim().toLowerCase();
  const searchResults =
    q.length >= 2 && allSchools
      ? allSchools
          .filter((s) => {
            const hay = `${s.name} ${s.city ?? ''} ${s.state ?? ''}`.toLowerCase();
            return hay.includes(q);
          })
          .slice(0, 20)
      : [];

  // Quick picks: target schools not already filtered out by the query.
  const quickPicks = targetSchoolNames.filter(
    (n) => !q || n.toLowerCase().includes(q)
  );

  function pick(name: string) {
    onChange(name || null);
    setQuery('');
    setOpen(false);
  }

  return (
    <div ref={containerRef} className="relative">
      {/* The control — shows the chosen school with a monogram, or a prompt. */}
      <button
        type="button"
        id={id}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 rounded-md border border-line px-2.5 py-2 text-left text-sm"
      >
        {value ? (
          <span className="flex items-center gap-2 min-w-0">
            <Monogram name={value} size={22} />
            <span className="truncate text-ink">{value}</span>
          </span>
        ) : (
          <span className="text-soft">No school linked</span>
        )}
        <span className="flex items-center gap-1 shrink-0">
          {value && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); pick(''); }}
              className="text-soft hover:text-soft"
              aria-label="Clear linked school"
            >
              <X size={14} />
            </button>
          )}
          <ChevronDown size={15} className="text-soft" />
        </span>
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full rounded-md border border-line bg-paper shadow-lg">
          {/* Search box */}
          <div className="flex items-center gap-1.5 border-b border-line px-2">
            <Search size={14} className="text-soft shrink-0" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search any school…"
              className="w-full py-2 text-sm outline-none bg-transparent"
            />
          </div>

          <div className="max-h-64 overflow-auto">
            {/* Quick picks from target schools */}
            {quickPicks.length > 0 && (
              <div>
                <div className="px-3 pt-2 pb-1 text-xs font-medium text-soft">
                  Target schools
                </div>
                {quickPicks.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => pick(name)}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-forest-50"
                  >
                    <Monogram name={name} size={20} />
                    <span className="truncate">{name}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Search results from the full dataset */}
            {searchResults.length > 0 && (
              <div>
                <div className="px-3 pt-2 pb-1 text-xs font-medium text-soft">
                  All schools
                </div>
                {searchResults.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => pick(s.name)}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-forest-50"
                  >
                    <Monogram name={s.name} size={20} />
                    <span className="min-w-0">
                      <span className="block truncate text-ink">{s.name}</span>
                      <span className="block truncate text-xs text-soft">
                        {[s.city, s.state].filter(Boolean).join(', ')}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}

            {/* States */}
            {q.length >= 2 && allSchools && searchResults.length === 0 &&
             quickPicks.length === 0 && (
              <div className="px-3 py-2 text-sm text-soft">
                No schools match "{query}".
              </div>
            )}
            {q.length < 2 && quickPicks.length === 0 && (
              <div className="px-3 py-2 text-sm text-soft">
                Type at least 2 characters to search.
              </div>
            )}
            {q.length >= 2 && !allSchools && (
              <div className="px-3 py-2 text-sm text-soft">Loading schools…</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
